import React, { useState, useRef, useEffect, useCallback } from 'react';
import Hls from 'hls.js';
import axios from 'axios';
import { Video, TriangleAlert, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import XTAMSpinner from './XTAMSpinner';
import './VideoCard.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3009/api/v1';

const MAX_DIGITAL_ZOOM = (() => {
  const raw = Number(process.env.REACT_APP_MAX_DIGITAL_ZOOM);
  if (!raw || isNaN(raw)) return 2;
  return Math.min(4, Math.max(1.1, raw / 100));
})();

/** Página HTML de MediaMTX (no WHEP / no HLS). */
function isMediaMtxHtmlPlayerUrl(url?: string): boolean {
  if (!url || url.includes('.m3u8')) return false;
  if (url.startsWith('rtsp://')) return false;
  // http(s) vivo = player HTML de MediaMTX (campo streams.webrtc sin /whep)
  return /^https?:\/\//i.test(url);
}

/** URL pública para iframe (sin userinfo, sin /whep). */
function toMediaMtxHtmlPlayerSrc(url: string): string {
  const cleaned = url.replace(/^(https?:\/\/)([^/@]+)@/i, '$1');
  try {
    const u = new URL(cleaned);
    u.username = '';
    u.password = '';
    let path = u.pathname.replace(/\/+$/, '');
    if (path.endsWith('/whep')) {
      path = path.slice(0, -'/whep'.length) || '/';
    }
    u.pathname = path;
    u.search = '';
    u.hash = '';
    return u.toString();
  } catch {
    return cleaned.replace(/\/whep\/?$/, '');
  }
}

function resolveLivePlayerUrl(streamUrl?: string): string | undefined {
  return streamUrl;
}

interface VideoCardProps {
  camera: {
    id: string;
    name: string;
    status: string;
    streamUrl?: string;
    isOnvif?: boolean;
    site?: {
      id?: string;
      name: string;
    };
  };
  onFullscreen?: () => void;
  onClose?: () => void;
  reconnectAttempts?: number;
  isReconnecting?: boolean;
  onReconnect?: (cameraId: string) => void;
}

const VideoCard: React.FC<VideoCardProps> = ({ 
  camera, 
  onFullscreen, 
  onClose,
  reconnectAttempts = 0,
  isReconnecting = false,
  onReconnect
}) => {
  const { token } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [reconnectCount, setReconnectCountState] = useState(0);
  const setReconnectCount = (v: number) => { reconnectCountRef.current = v; setReconnectCountState(v); };
  const [isLoading, setIsLoading] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  /** Pestaña oculta: pausar iframe (suelta lectores MediaMTX); al volver se reanuda. */
  const [tabVisible, setTabVisible] = useState(
    () => typeof document === 'undefined' || document.visibilityState !== 'hidden',
  );
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const webrtcPcRef = useRef<RTCPeerConnection | null>(null);
  const whepSessionUrlRef = useRef<string | null>(null);
  const webrtcAbortRef = useRef<AbortController | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasManifestParsedRef = useRef(false);
  const canPlayHandlerRef = useRef<(() => void) | null>(null);
  const processedHlsUrlRef = useRef<string | null>(null);
  const manifestRetryCountRef = useRef(0);
  const reconnectCountRef = useRef(0);
  const playingHandlerRef = useRef<(() => void) | null>(null);
  const webrtcActiveUrlRef = useRef<string | null>(null);
  const webrtcDisconnectTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [webrtcRetryTrigger, setWebrtcRetryTrigger] = useState(0);
  const webrtcRetryCountRef = useRef(0);
  const [zoom, setZoom] = useState(1);
  const zoomRef = useRef(1);
  const [zoomOrigin, setZoomOrigin] = useState({ x: 50, y: 50 }); // porcentaje dentro del contenedor
  const [isPanning, setIsPanning] = useState(false);
  const [showPtz, setShowPtz] = useState(false);
  const [ptzLoading, setPtzLoading] = useState(false);
  const [ptzError, setPtzError] = useState<string | null>(null);
  const [isPtzDragging, setIsPtzDragging] = useState(false);
  const ptzDragStartRef = useRef<{ x: number; y: number } | null>(null);
  const ptzWheelStopTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showPtzRef = useRef(showPtz);
  useEffect(() => { showPtzRef.current = showPtz; }, [showPtz]);
  const panStartRef = useRef<{ x: number; y: number; originX: number; originY: number; pointerId: number | null }>({
    x: 0,
    y: 0,
    originX: 50,
    originY: 50,
    pointerId: null,
  });
  // Punto de enfoque fijo durante un gesto de zoom (evita saltos al mover ligeramente el cursor)
  const zoomFocusLockRef = useRef<{ x: number; y: number } | null>(null);
  const zoomFocusLockTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ZOOM_FOCUS_LOCK_MS = 280;

  useEffect(() => { zoomRef.current = zoom; }, [zoom]);

  useEffect(() => {
    return () => {
      if (zoomFocusLockTimeoutRef.current) {
        clearTimeout(zoomFocusLockTimeoutRef.current);
      }
      if (ptzWheelStopTimeoutRef.current) {
        clearTimeout(ptzWheelStopTimeoutRef.current);
      }
    };
  }, []);

  const getStatusColor = () => {
    if (isReconnecting) return '#f59e0b'; // orange for reconnecting
    if (camera.status === 'active' || camera.status === 'running') return '#10b981'; // green
    if (camera.status === 'error' || camera.status === 'offline' || camera.status === 'crashed') {
      if (reconnectAttempts >= 3) return '#ef4444'; // red for failed
      return '#f59e0b'; // orange for attempting
    }
    return '#6b7280'; // gray
  };

  const getStatusText = () => {
    if (isReconnecting) return `RECONECTANDO (${reconnectAttempts}/3)`;
    if (camera.status === 'active' || camera.status === 'running') return 'ONLINE';
    if (camera.status === 'error' || camera.status === 'crashed') {
      if (reconnectAttempts >= 3) return 'CONEXIÓN FALLIDA';
      return 'ERROR';
    }
    if (camera.status === 'offline') return 'OFFLINE';
    if (camera.status === 'starting') return 'INICIANDO...';
    return 'INACTIVO';
  };

  const authHeaders = useCallback(() => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [token]);

  const parseWebrtcUrl = useCallback((urlWithCreds: string) => {
    const u = new URL(urlWithCreds);
    const username = u.username || '';
    const password = u.password || '';
    // limpiar credenciales del URL para usarlo en fetch (evita problemas de CORS/seguridad del navegador)
    u.username = '';
    u.password = '';
    return {
      endpoint: u.toString(),
      authHeader: (username || password) ? `Basic ${btoa(`${username}:${password}`)}` : null,
    };
  }, []);

  const disconnectWebrtc = useCallback(async (webrtcUrl?: string) => {
    // Capture and clear refs SYNCHRONOUSLY first to prevent race conditions
    // with retry logic that may create a new PC before this async function completes
    const sessionUrl = whepSessionUrlRef.current;
    const abort = webrtcAbortRef.current;
    const pc = webrtcPcRef.current;

    whepSessionUrlRef.current = null;
    webrtcAbortRef.current = null;
    webrtcPcRef.current = null;

    if (pc) {
      try { pc.close(); } catch { /* ignore */ }
    }
    if (abort) {
      try { abort.abort(); } catch { /* ignore */ }
    }

    const video = videoRef.current;
    if (video?.srcObject) {
      try {
        (video.srcObject as MediaStream).getTracks().forEach((t) => t.stop());
      } catch { /* ignore */ }
      video.srcObject = null;
    }

    // Async: best-effort DELETE de sesión WHEP (ya no toca refs)
    if (sessionUrl) {
      try {
        const authHeader = webrtcUrl ? parseWebrtcUrl(webrtcUrl).authHeader : null;
        await fetch(sessionUrl, {
          method: 'DELETE',
          headers: {
            ...(authHeader ? { Authorization: authHeader } : {}),
          },
        });
      } catch { /* ignore */ }
    }
  }, [parseWebrtcUrl]);

  const createOfferSdp = useCallback(async (pc: RTCPeerConnection, timeoutMs = 5000) => {
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    // Esperar ICE gathering (mejor compatibilidad cuando no hay trickle ICE)
    if (pc.iceGatheringState !== 'complete') {
      await new Promise<void>((resolve) => {
        const to = setTimeout(() => {
          pc.removeEventListener('icegatheringstatechange', onChange);
          resolve();
        }, timeoutMs);
        function onChange() {
          if (pc.iceGatheringState === 'complete') {
            clearTimeout(to);
            pc.removeEventListener('icegatheringstatechange', onChange);
            resolve();
          }
        }
        pc.addEventListener('icegatheringstatechange', onChange);
      });
    }

    return pc.localDescription?.sdp || offer.sdp || '';
  }, []);

  const ptzMove = useCallback(
    async (speed: { x: number; y: number; z: number }) => {
      if (!camera.isOnvif) return;
      setPtzError(null);
      try {
        await axios.post(
          `${API_URL}/cameras/${camera.id}/onvif/ptz/move`,
          { speed, timeout: 60 },
          { headers: authHeaders() }
        );
      } catch (e: any) {
        const apiMsg = e.response?.data?.error?.message || e.response?.data?.message || e.message;
        setPtzError(apiMsg || 'Error PTZ move');
      }
    },
    [camera.id, camera.isOnvif, authHeaders]
  );

  const ptzStop = useCallback(async () => {
    if (!camera.isOnvif) return;
    setPtzError(null);
    try {
      await axios.post(`${API_URL}/cameras/${camera.id}/onvif/ptz/stop`, {}, { headers: authHeaders() });
    } catch (e: any) {
      const apiMsg = e.response?.data?.error?.message || e.response?.data?.message || e.message;
      setPtzError(apiMsg || 'Error PTZ stop');
    }
  }, [camera.id, camera.isOnvif, authHeaders]);

  const ptzHome = useCallback(async () => {
    if (!camera.isOnvif) return;
    setPtzError(null);
    try {
      await axios.post(`${API_URL}/cameras/${camera.id}/onvif/ptz/home`, { speed: 1 }, { headers: authHeaders() });
    } catch (e: any) {
      const apiMsg = e.response?.data?.error?.message || e.response?.data?.message || e.message;
      setPtzError(apiMsg || 'Error PTZ home');
    }
  }, [camera.id, camera.isOnvif, authHeaders]);

  useEffect(() => {
    if (!showPtz) setPtzError(null);
  }, [showPtz]);

  const livePlayerUrl = resolveLivePlayerUrl(camera.streamUrl);

  useEffect(() => {
    const onVisibility = () => {
      const visible = document.visibilityState !== 'hidden';
      setTabVisible(visible);
      if (!visible) {
        // Al ocultar: liberar WHEP si hubiera sesión activa.
        if (webrtcActiveUrlRef.current) {
          disconnectWebrtc(webrtcActiveUrlRef.current).catch(() => {});
          webrtcActiveUrlRef.current = null;
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [disconnectWebrtc]);

  useEffect(() => {
    // Vivo MediaMTX: página HTML en iframe (sin WHEP / RTCPeerConnection).
    if (livePlayerUrl && isMediaMtxHtmlPlayerUrl(livePlayerUrl)) {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      if (webrtcActiveUrlRef.current) {
        disconnectWebrtc(webrtcActiveUrlRef.current).catch(() => {});
        webrtcActiveUrlRef.current = null;
      }
      setError(null);
      // Cold start on-demand: 2–10s hasta primer frame; mantener "conectando".
      setIsLoading(true);
      const connectTimer = window.setTimeout(() => setIsLoading(false), 20000);
      return () => window.clearTimeout(connectTimer);
    }

    const video = videoRef.current;
    
    // Intentar cargar si hay streamUrl, incluso si el status es 'starting'.
    // Motivo: el backend/WebSocket puede tardar en notificar "running", pero el HLS puede ya estar disponible.
    if (!video || !livePlayerUrl || (camera.status !== 'active' && camera.status !== 'running' && camera.status !== 'starting')) {
      if (!livePlayerUrl) {
        setError(null);
        setIsLoading(false);
        // Disconnect WebRTC if stream URL was cleared (e.g. manual reconnect flow)
        if (webrtcActiveUrlRef.current) {
          disconnectWebrtc(webrtcActiveUrlRef.current).catch(() => {});
          webrtcActiveUrlRef.current = null;
        }
        if (webrtcDisconnectTimerRef.current) {
          clearTimeout(webrtcDisconnectTimerRef.current);
          webrtcDisconnectTimerRef.current = null;
        }
      } else if (camera.status === 'starting') {
        setIsLoading(true);
        setError(null);
        if (hlsRef.current) {
          hlsRef.current.destroy();
          hlsRef.current = null;
        }
      }
      return;
    }

    // Tras el guard, livePlayerUrl está definido.
    const streamUrl = livePlayerUrl;

    // Guard: if WebRTC is already connected to the same URL and healthy, skip re-setup.
    // Prevents tearing down a working connection when only camera.status changes
    // (e.g. 'starting' → 'active' via WebSocket).
    if (streamUrl.includes('/whep') &&
        webrtcActiveUrlRef.current === streamUrl &&
        webrtcPcRef.current &&
        !['failed', 'closed'].includes(webrtcPcRef.current.iceConnectionState)) {
      const pcState = webrtcPcRef.current.iceConnectionState;
      if (pcState === 'connected' || pcState === 'completed') {
        setIsLoading(false);
        setError(null);
      }
      return;
    }

    if (!reconnectTimeoutRef.current) {
      setReconnectCount(0);
      manifestRetryCountRef.current = 0;
    }
    webrtcRetryCountRef.current = 0;
    setError(null);
    setIsLoading(true);
    hasManifestParsedRef.current = false;

    // Limpiar HLS anterior
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    // Limpiar WebRTC anterior solo si la URL cambió
    if (webrtcActiveUrlRef.current && webrtcActiveUrlRef.current !== streamUrl) {
      disconnectWebrtc(webrtcActiveUrlRef.current).catch(() => {});
      webrtcActiveUrlRef.current = null;
    }
    if (webrtcDisconnectTimerRef.current) {
      clearTimeout(webrtcDisconnectTimerRef.current);
      webrtcDisconnectTimerRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }

    // Si el stream es WebRTC (WHEP)
    if (streamUrl.includes('/whep')) {
      const webrtcUrl = streamUrl;
      setIsLoading(true);
      setError(null);

      // Asegurar que el elemento <video> esté limpio
      try {
        video.pause();
      } catch {
        // ignore
      }
      video.src = '';
      video.srcObject = null;

      const controller = new AbortController();
      webrtcAbortRef.current = controller;

      const pc = new RTCPeerConnection({
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' },
        ],
        iceCandidatePoolSize: 10,
      });
      webrtcPcRef.current = pc;

      pc.ontrack = (event) => {
        if (!videoRef.current || webrtcPcRef.current !== pc) return;
        videoRef.current.srcObject = event.streams[0];
        setIsLoading(false);
        setError(null);
      };

      pc.oniceconnectionstatechange = () => {
        if (webrtcPcRef.current !== pc) return;
        const state = pc.iceConnectionState;

        if (webrtcDisconnectTimerRef.current) {
          clearTimeout(webrtcDisconnectTimerRef.current);
          webrtcDisconnectTimerRef.current = null;
        }

        if (state === 'connected' || state === 'completed') {
          setIsLoading(false);
          setError(null);
          webrtcRetryCountRef.current = 0;
        } else if (state === 'disconnected') {
          webrtcDisconnectTimerRef.current = setTimeout(() => {
            const currentState = pc.iceConnectionState;
            if (currentState === 'disconnected' || currentState === 'failed' || currentState === 'closed') {
              if (webrtcRetryCountRef.current < 10) {
                webrtcRetryCountRef.current++;
                setIsLoading(true);
                setError(null);
                disconnectWebrtc(webrtcUrl).catch(() => {});
                webrtcActiveUrlRef.current = null;
                setWebrtcRetryTrigger(prev => prev + 1);
              } else {
                setError('Conexión WebRTC perdida');
                setIsLoading(false);
                webrtcActiveUrlRef.current = null;
              }
            }
          }, 3000);
        } else if (state === 'failed' || state === 'closed') {
          if (webrtcRetryCountRef.current < 10) {
            webrtcRetryCountRef.current++;
            setIsLoading(true);
            setError(null);
            disconnectWebrtc(webrtcUrl).catch(() => {});
            webrtcActiveUrlRef.current = null;
            setTimeout(() => setWebrtcRetryTrigger(prev => prev + 1), 2000);
          } else {
            setError('Conexión WebRTC perdida');
            setIsLoading(false);
            webrtcActiveUrlRef.current = null;
          }
        }
      };

      pc.addTransceiver('video', { direction: 'recvonly' });
      pc.addTransceiver('audio', { direction: 'recvonly' });

      (async () => {
        const MAX_WHEP_RETRIES = 5;
        for (let attempt = 0; attempt < MAX_WHEP_RETRIES; attempt++) {
          if (controller.signal.aborted) return;
          try {
            const { endpoint, authHeader } = parseWebrtcUrl(webrtcUrl);
            const offerSdp = await createOfferSdp(pc);

            const res = await fetch(endpoint, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/sdp',
                ...(authHeader ? { Authorization: authHeader } : {}),
              },
              body: offerSdp,
              signal: controller.signal,
            });

            if (!res.ok) {
              const txt = await res.text().catch(() => '');
              if (attempt < MAX_WHEP_RETRIES - 1) {
                const delay = Math.min(1000 * (attempt + 1), 3000);
                await new Promise(r => setTimeout(r, delay));
                continue;
              }
              throw new Error(txt || `HTTP ${res.status}`);
            }

            const locationHeader = res.headers.get('Location');
            const linkHeader = res.headers.get('Link');
            const rawSessionUrl =
              locationHeader ||
              (linkHeader ? (linkHeader.match(/<([^>]+)>/) || [])[1] : null);
            if (rawSessionUrl) {
              whepSessionUrlRef.current = new URL(rawSessionUrl, endpoint).toString();
            } else {
              whepSessionUrlRef.current = null;
            }

            const answerSdp = await res.text();
            await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
            webrtcActiveUrlRef.current = webrtcUrl;
            return;
          } catch (e: any) {
            if (e?.name === 'AbortError') return;
            if (attempt < MAX_WHEP_RETRIES - 1) {
              const delay = Math.min(1000 * (attempt + 1), 3000);
              await new Promise(r => setTimeout(r, delay));
              continue;
            }
            setError(e?.message || 'Error al conectar WebRTC');
            setIsLoading(false);
            webrtcActiveUrlRef.current = null;
            disconnectWebrtc(webrtcUrl).catch(() => {});
          }
        }
      })();

      // No cleanup function here — WebRTC disconnect is handled by:
      // 1. The guard + beginning-of-effect cleanup (when URL changes)
      // 2. The unmount-only cleanup effect
      // 3. The initial guard (when streamUrl becomes undefined)
      return;
    }

    // Si el stream es HLS
    if (streamUrl.includes('.m3u8')) {
      if (Hls.isSupported()) {
        // Extraer credenciales de la URL si existen
        let hlsUrl = streamUrl;
        let xhrSetup: ((xhr: XMLHttpRequest, url: string) => void) | undefined;
        
        // Si la URL tiene credenciales (formato: http://user:pass@host/path)
        const urlMatch = streamUrl.match(/^https?:\/\/([^:]+):([^@]+)@(.+)$/);
        if (urlMatch) {
          const [, username, password, restOfUrl] = urlMatch;
          hlsUrl = `http://${restOfUrl}`; // URL sin credenciales
          
          // Configurar autenticación HTTP básica
          xhrSetup = (xhr: XMLHttpRequest) => {
            const auth = btoa(`${username}:${password}`);
            xhr.setRequestHeader('Authorization', `Basic ${auth}`);
          };
        }
        
        // Guardar la URL procesada para poder reutilizarla en reintentos
        processedHlsUrlRef.current = hlsUrl;
        
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
          backBufferLength: 90,
          maxBufferLength: 30,           // Buffer máximo más corto para menor latencia
          maxMaxBufferLength: 60,         // Límite máximo del buffer
          maxBufferSize: 60 * 1000 * 1000, // 60MB máximo de buffer
          maxBufferHole: 0.5,             // Tolerancia para gaps en el buffer
          highBufferWatchdogPeriod: 2,    // Verificar buffer alto cada 2 segundos
          nudgeOffset: 0.1,               // Ajuste fino para sincronización
          nudgeMaxRetry: 3,               // Reintentos para ajuste
          // Nota: los timeouts en Hls.js están en MILISEGUNDOS (ms)
          fragLoadingTimeOut: 10000,       // 10s
          manifestLoadingTimeOut: 10000,   // 10s
          // Reintentos internos más agresivos para streams "on-demand" (cold start)
          // Esto reduce el tiempo hasta primer frame cuando el manifest aún no existe por unos instantes.
          manifestLoadingMaxRetry: 6,
          manifestLoadingRetryDelay: 500,
          manifestLoadingMaxRetryTimeout: 4000,
          fragLoadingMaxRetry: 6,
          fragLoadingRetryDelay: 500,
          fragLoadingMaxRetryTimeout: 4000,
          xhrSetup: xhrSetup,             // Configurar autenticación si es necesaria
        });

        hls.loadSource(hlsUrl);
        hls.attachMedia(video);
        
        // Timeout para evitar que se quede en loading indefinidamente
        loadingTimeoutRef.current = setTimeout(() => {
          if (!hasManifestParsedRef.current) {
            console.warn(`[VideoCard] ⚠️ Timeout cargando stream para ${camera.id} después de 15s`);
            // Si después de 15 segundos no se ha parseado el manifest, puede ser un problema
            // Pero no mostrar error todavía, puede estar cargando lentamente
            // Solo loguear para debugging
          }
        }, 15000);
        
        // Handler para cuando el video puede reproducir
        const handleCanPlay = () => {
          if (!hasManifestParsedRef.current) {
            if (loadingTimeoutRef.current) {
              clearTimeout(loadingTimeoutRef.current);
              loadingTimeoutRef.current = null;
            }
            hasManifestParsedRef.current = true;
            setIsLoading(false);
            setError(null);
          }
        };
        
        // Guardar referencia al handler para poder removerlo después
        canPlayHandlerRef.current = handleCanPlay;
        
        // Agregar listener para cuando el video puede reproducir
        video.addEventListener('canplay', handleCanPlay);
        
        const handlePlaying = () => {
          if (!hasManifestParsedRef.current) {
            if (loadingTimeoutRef.current) {
              clearTimeout(loadingTimeoutRef.current);
              loadingTimeoutRef.current = null;
            }
            hasManifestParsedRef.current = true;
            setIsLoading(false);
            setError(null);
          }
        };
        playingHandlerRef.current = handlePlaying;
        video.addEventListener('playing', handlePlaying, { once: true });
        
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          if (loadingTimeoutRef.current) {
            clearTimeout(loadingTimeoutRef.current);
            loadingTimeoutRef.current = null;
          }
          // Manifest listo ≠ primer frame decodificable (p. ej. tras transcoding o GOP largo).
          // No quitar el loading aquí: evita pantalla negra con spinner ya oculto.
          hasManifestParsedRef.current = true;
          setError(null);
          setReconnectCount(0); // Resetear contador en éxito
          manifestRetryCountRef.current = 0; // Resetear contador de manifest también
          video.play().catch((err: any) => {
            // Ignorar errores de "background media paused" - es normal cuando la pestaña no está activa
            if (err.name === 'AbortError' && 
                (err.message?.includes('background media') || err.message?.includes('paused to save power'))) {
              // El navegador pausó el video para ahorrar energía, esto es normal
              // El video se reproducirá automáticamente cuando la pestaña vuelva a estar activa
              return;
            }
            // Solo mostrar errores críticos
            if (err.name !== 'NotAllowedError' && err.name !== 'NotSupportedError') {
              console.error('Error al reproducir:', err);
              setError('Error al reproducir el stream');
            }
          });
        });

        hls.on(Hls.Events.FRAG_LOADED, () => {
          // Cuando se carga el primer fragmento, el stream está realmente funcionando
          if (!hasManifestParsedRef.current) {
            if (loadingTimeoutRef.current) {
              clearTimeout(loadingTimeoutRef.current);
              loadingTimeoutRef.current = null;
            }
            hasManifestParsedRef.current = true;
            setIsLoading(false);
            setError(null);
          }
        });

        // FRAG_PARSING_DATA no existe en HLS.js, usar FRAG_LOADED que ya está arriba
        // Este evento ya está manejado arriba con FRAG_LOADED

        hls.on(Hls.Events.ERROR, (event, data) => {
          if (data.fatal) {
            const resp = data.response as { text?: string; data?: string | unknown; responseText?: string } | undefined;
            let respBody = typeof resp?.text === 'string' ? resp.text : (typeof resp?.data === 'string' ? resp.data : '');
            if (!respBody && typeof (resp as any)?.responseText === 'string') respBody = (resp as any).responseText;
            if (!respBody && resp && typeof (resp as any).data === 'object' && (resp as any).data !== null) {
              const obj = (resp as any).data as { status?: string; error?: string };
              if (obj?.status === 'error' && /no stream/i.test(String(obj?.error ?? ''))) respBody = JSON.stringify(obj);
            }
            const isNoStreamResponse = (body: string | undefined) => {
              if (!body || typeof body !== 'string') return false;
              if (/no stream is available/i.test(body.trim())) return true;
              try {
                const parsed = JSON.parse(body.trim()) as { status?: string; error?: string };
                return parsed?.status === 'error' && typeof parsed?.error === 'string' && /no stream/i.test(parsed.error);
              } catch {
                return false;
              }
            };
            const noStreamMsg = isNoStreamResponse(respBody) ? 'SIN SEÑAL' : null;

            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                // Detectar específicamente errores 404 en el manifest (stream aún no disponible)
                const isManifest404 = data.details === 'manifestLoadError' && 
                                      data.response && 
                                      data.response.code === 404;
                // Detectar errores de autenticación del manifest (MediaMTX requiere Basic Auth)
                const isManifest401 = data.details === 'manifestLoadError' &&
                                      data.response &&
                                      data.response.code === 401;
                
                if (isManifest404 && !hasManifestParsedRef.current) {
                  // El manifest aún no existe, probablemente el stream se está iniciando
                  // Usar ref para mantener el contador persistente entre re-renders
                  manifestRetryCountRef.current += 1;
                  const currentCount = manifestRetryCountRef.current;
                  
                  // Mantener el loading y reintentar de forma más tolerante.
                  // Esto evita que el player “se rinda” justo antes de que el HLS esté disponible.
                  setReconnectCount(currentCount);
                  setIsLoading(true);
                  // Después de algunos intentos, mostrar una pista pero sin cortar el proceso.
                  if (currentCount >= 5) {
                    setError('El stream está tardando en iniciar. Seguiremos intentando… (si se queda pegado, usa "Reconectar").');
                  } else {
                    setError(null);
                  }

                  // Backoff:
                  // - primeros intentos: 0.5s, 1s, 2s, 4s
                  // - luego: cada 8s (para no saturar)
                  // - después de muchos intentos: cada 15s
                  const delay =
                    currentCount < 5
                      ? Math.min(500 * Math.pow(2, currentCount - 1), 4000)
                      : currentCount < 20
                        ? 8000
                        : 15000;
                  console.log(`[VideoCard] ⏳ Manifest no disponible aún para ${camera.id}, reintentando en ${delay / 1000}s (intento ${currentCount})...`);

                  if (reconnectTimeoutRef.current) {
                    clearTimeout(reconnectTimeoutRef.current);
                    reconnectTimeoutRef.current = null;
                  }
                  reconnectTimeoutRef.current = setTimeout(() => {
                    if (hlsRef.current && video && processedHlsUrlRef.current) {
                      console.log(`[VideoCard] 🔄 Reintentando cargar manifest para ${camera.id} (intento ${manifestRetryCountRef.current})...`);
                      hlsRef.current.loadSource(processedHlsUrlRef.current);
                      hlsRef.current.startLoad();
                    }
                  }, delay);
                  return;
                }

                if (isManifest401 && !hasManifestParsedRef.current) {
                  // Sin credenciales (o no se envió Authorization). No vale la pena spamear reintentos.
                  console.error(`[VideoCard] ❌ Manifest 401 (Unauthorized) para ${camera.id}`);
                  setError('MediaMTX requiere autenticación para HLS. Verifica que la URL incluya credenciales (viewer) o ajusta el backend.');
                  setIsLoading(false);
                  return;
                }
                
                console.error('Error de red HLS:', data);
                // Solo intentar reconectar si ya se había cargado el manifest antes
                // Esto evita errores prematuros cuando el stream está iniciando
                if (!hasManifestParsedRef.current && !isManifest404) {
                  setIsLoading(true);
                  return; // No hacer nada, esperar a que cargue el manifest
                }
                
                if (reconnectCountRef.current < 3) {
                  const newCount = reconnectCountRef.current + 1;
                  setReconnectCount(newCount);
                  setError(`Conexión perdida. Reintentando... (${newCount}/3)`);
                  setIsLoading(true);
                  const delay = Math.min(2000 * Math.pow(2, newCount - 1), 8000);
                  reconnectTimeoutRef.current = setTimeout(() => {
                    console.log(`[VideoCard] Reintentando cargar stream ${camera.id} (intento ${newCount + 1}/3)...`);
                    if (hlsRef.current) {
                      hlsRef.current.startLoad();
                    }
                  }, delay);
                } else {
                  console.error('Máximo de intentos de reconexión alcanzado');
                  if (hlsRef.current) {
                    hlsRef.current.destroy();
                  }
                  setError(noStreamMsg ?? 'Error: No se pudo reconectar después de 3 intentos');
                  setIsLoading(false);
                }
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                console.error('Error de media HLS:', data);
                // Intentar recuperar automáticamente
                if (hlsRef.current) {
                  hlsRef.current.recoverMediaError();
                }
                break;
              default:
                console.error('Error fatal HLS:', data);
                if (hlsRef.current) {
                  hlsRef.current.destroy();
                }
                setError(noStreamMsg ?? 'Error al cargar el stream');
                setIsLoading(false);
                break;
            }
          } else {
            // Errores no fatales - manejar según el tipo
            if (data.type === Hls.ErrorTypes.MEDIA_ERROR && data.details === 'bufferStalledError') {
              // Buffer bajo - esto es normal en streams en vivo, no es un error crítico
              // Solo loguear en desarrollo, no mostrar error al usuario
              if (process.env.NODE_ENV === 'development') {
              }
              // No hacer nada, HLS.js manejará esto automáticamente
            } else {
              // Otros errores no fatales, solo loguear
              console.warn('Error HLS no fatal:', data);
            }
          }
        });

        hlsRef.current = hls;
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // Soporte nativo de HLS (Safari)
        // Safari no soporta credenciales en la URL, usar fetch con autenticación
        let hlsUrl = streamUrl;
        const urlMatch = streamUrl.match(/^https?:\/\/([^:]+):([^@]+)@(.+)$/);
        
        if (urlMatch) {
          const [, username, password, restOfUrl] = urlMatch;
          hlsUrl = `http://${restOfUrl}`;
          
          // Para Safari, necesitamos usar un blob URL con autenticación
          // Por ahora, intentar sin credenciales (si MediaMTX permite acceso público)
          // O usar un proxy en el backend
        }
        
        video.src = hlsUrl;
        video.play().catch((err: any) => {
          // Ignorar errores de "background media paused" - es normal cuando la pestaña no está activa
          if (err.name === 'AbortError' && 
              (err.message?.includes('background media') || err.message?.includes('paused to save power'))) {
            return; // Error no crítico, ignorar silenciosamente
          }
          // Solo mostrar errores críticos
          if (err.name !== 'NotAllowedError' && err.name !== 'NotSupportedError') {
            console.error('Error al reproducir:', err);
            setError('Error al reproducir el stream');
          }
        });
      }
    } else {
      // Stream directo (MP4, WebM, etc.)
      video.src = streamUrl;
      video.play().catch((err: any) => {
        // Ignorar errores de "background media paused" - es normal cuando la pestaña no está activa
        if (err.name === 'AbortError' && 
            (err.message?.includes('background media') || err.message?.includes('paused to save power'))) {
          return; // Error no crítico, ignorar silenciosamente
        }
        // Solo mostrar errores críticos
        if (err.name !== 'NotAllowedError' && err.name !== 'NotSupportedError') {
          console.error('Error al reproducir:', err);
          setError('Error al reproducir el stream');
        }
      });
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      if (videoRef.current) {
        if (canPlayHandlerRef.current) {
          videoRef.current.removeEventListener('canplay', canPlayHandlerRef.current);
          canPlayHandlerRef.current = null;
        }
        if (playingHandlerRef.current) {
          videoRef.current.removeEventListener('playing', playingHandlerRef.current);
          playingHandlerRef.current = null;
        }
      }
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      // WebRTC cleanup is NOT done here — it's handled by the guard at the
      // beginning of this effect (URL-change) and the unmount-only effect below.
    };
  }, [livePlayerUrl, camera.status, camera.id, createOfferSdp, disconnectWebrtc, parseWebrtcUrl, webrtcRetryTrigger]);

  // When status changes to active/running, clear errors but DON'T reset isLoading
  // to avoid flashing a loading overlay on top of an already-playing WebRTC stream.
  useEffect(() => {
    if ((camera.status === 'active' || camera.status === 'running') && camera.streamUrl) {
      if (!reconnectTimeoutRef.current) {
        setReconnectCount(0);
        setError(null);
      }
    }
  }, [camera.status, camera.streamUrl, camera.id]);

  // Unmount-only cleanup for WebRTC (connection lifecycle is managed in the main effect body)
  useEffect(() => {
    return () => {
      if (webrtcActiveUrlRef.current) {
        disconnectWebrtc(webrtcActiveUrlRef.current).catch(() => {});
        webrtcActiveUrlRef.current = null;
      }
      if (webrtcDisconnectTimerRef.current) {
        clearTimeout(webrtcDisconnectTimerRef.current);
        webrtcDisconnectTimerRef.current = null;
      }
    };
  }, [disconnectWebrtc]);

  // Notificar al backend cuando el usuario está viendo el stream (streaming bajo demanda)
  useEffect(() => {
    // Notificar si hay URL (aunque el status aún sea starting).
    // Esto alinea "viewer presente" con el hecho de que el usuario ya montó el player.
    const effectiveUrl = resolveLivePlayerUrl(camera.streamUrl);
    const isStreamActive = (camera.status === 'active' || camera.status === 'running' || camera.status === 'starting') &&
                          effectiveUrl &&
                          token;

    if (!isStreamActive) {
      return;
    }

    const viewStartedAtMs = Date.now();

    // Notificar que hay un viewer cuando el componente se monta o el stream se activa
    const notifyViewerStart = async () => {
      try {
        await axios.post(
          `${API_URL}/streams/viewer/start`,
          { streamId: camera.id },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error: any) {
        // No mostrar error al usuario, solo loguear
        console.warn(`[VideoCard] Error notificando viewer start para ${camera.id}:`, error.message);
      }
    };

    notifyViewerStart();

    // Heartbeat: evita viewers fantasma si el navegador se cierra / pierde el unload.
    // Best-effort: si falla, no rompemos UX.
    const heartbeatInterval = setInterval(() => {
      axios.post(
        `${API_URL}/streams/viewer/heartbeat`,
        { streamId: camera.id },
        { headers: { Authorization: `Bearer ${token}` } }
      ).catch(() => {
        // silencioso
      });
    }, 30000); // cada 30s

    // Cleanup: notificar cuando el componente se desmonta o el stream se desactiva
    return () => {
      clearInterval(heartbeatInterval);
      const notifyViewerStop = async () => {
        const durationSeconds = Math.max(0, Math.round((Date.now() - viewStartedAtMs) / 1000));
        const siteIdRaw = camera.site?.id;
        const siteIdNum =
          siteIdRaw != null && siteIdRaw !== ''
            ? parseInt(String(siteIdRaw), 10)
            : NaN;
        try {
          await axios.post(
            `${API_URL}/streams/viewer/stop`,
            {
              streamId: camera.id,
              durationSeconds,
              ...(Number.isFinite(siteIdNum) ? { siteId: siteIdNum } : {}),
              ...(camera.site?.name ? { siteName: camera.site.name } : {}),
              cameraName: camera.name
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (error: any) {
          // No mostrar error al usuario, solo loguear
          console.warn(`[VideoCard] Error notificando viewer stop para ${camera.id}:`, error.message);
        }
      };

      notifyViewerStop();
    };
  }, [camera.id, camera.name, camera.status, camera.streamUrl, camera.site?.id, camera.site?.name, token]);

  const handleFullscreen = () => {
    if (onFullscreen) {
      onFullscreen();
    } else if (containerRef.current?.requestFullscreen) {
      // Fullscreen del contenedor (no del video) para conservar zoom digital y pan
      containerRef.current.requestFullscreen();
    }
  };

  const resetZoom = useCallback(() => {
    if (zoomFocusLockTimeoutRef.current) {
      clearTimeout(zoomFocusLockTimeoutRef.current);
      zoomFocusLockTimeoutRef.current = null;
    }
    zoomFocusLockRef.current = null;
    setZoom(1);
    setZoomOrigin({ x: 50, y: 50 });
    setIsPanning(false);
  }, []);

  const handleContainerDoubleClick = useCallback(() => {
    if (camera.isOnvif && showPtzRef.current) {
      ptzHome();
    } else {
      resetZoom();
    }
  }, [camera.isOnvif, ptzHome, resetZoom]);

  const handleWheelZoom = useCallback((e: React.WheelEvent) => {
    e.preventDefault();

    // Con PTZ activo: rueda = zoom óptico (PTZ), no zoom digital
    if (camera.isOnvif && showPtzRef.current) {
      const z = e.deltaY < 0 ? 0.5 : -0.5;
      ptzMove({ x: 0, y: 0, z });
      if (ptzWheelStopTimeoutRef.current) clearTimeout(ptzWheelStopTimeoutRef.current);
      ptzWheelStopTimeoutRef.current = setTimeout(() => {
        ptzStop();
        ptzWheelStopTimeoutRef.current = null;
      }, 120);
      return;
    }

    const el = containerRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const x = Math.min(100, Math.max(0, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.min(100, Math.max(0, ((e.clientY - rect.top) / rect.height) * 100));

    // Fijar el punto de enfoque al inicio del gesto de zoom (comportamiento tipo visor: sin saltos)
    if (zoomFocusLockRef.current === null) {
      zoomFocusLockRef.current = { x, y };
    }
    if (zoomFocusLockTimeoutRef.current) {
      clearTimeout(zoomFocusLockTimeoutRef.current);
    }
    zoomFocusLockTimeoutRef.current = setTimeout(() => {
      zoomFocusLockRef.current = null;
      zoomFocusLockTimeoutRef.current = null;
    }, ZOOM_FOCUS_LOCK_MS);

    const origin = zoomFocusLockRef.current;
    const direction = e.deltaY < 0 ? 1 : -1;
    const step = 0.12;
    const currentZoom = zoomRef.current;
    const nextZoom = Math.min(MAX_DIGITAL_ZOOM, Math.max(1, +(currentZoom + direction * step).toFixed(2)));

    if (nextZoom <= 1) {
      if (zoomFocusLockTimeoutRef.current) {
        clearTimeout(zoomFocusLockTimeoutRef.current);
        zoomFocusLockTimeoutRef.current = null;
      }
      zoomFocusLockRef.current = null;
      setZoom(1);
      setZoomOrigin({ x: 50, y: 50 });
      return;
    }

    setZoom(nextZoom);
    setZoomOrigin({ x: origin.x, y: origin.y });
  }, [camera.isOnvif, ptzMove, ptzStop]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    const el = containerRef.current;
    if (!el) return;

    if (camera.isOnvif && showPtzRef.current) {
      e.preventDefault();
      setIsPtzDragging(true);
      ptzDragStartRef.current = { x: e.clientX, y: e.clientY };
      try {
        el.setPointerCapture(e.pointerId);
      } catch {
        // ignore
      }
      return;
    }

    if (zoom <= 1) return;
    e.preventDefault();
    try {
      el.setPointerCapture(e.pointerId);
    } catch {
      // ignore
    }
    setIsPanning(true);
    panStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      originX: zoomOrigin.x,
      originY: zoomOrigin.y,
      pointerId: e.pointerId,
    };
  }, [camera.isOnvif, zoom, zoomOrigin.x, zoomOrigin.y]);

  const handlePointerMovePan = useCallback((e: React.PointerEvent) => {
    if (isPtzDragging) {
      const start = ptzDragStartRef.current;
      if (!start) return;
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      const distance = Math.hypot(dx, dy);
      if (distance < 4) return;
      const magnitude = Math.min(1, distance / 80);
      const nx = dx / distance;
      const ny = -dy / distance;
      const speed = magnitude * 0.7;
      ptzMove({ x: nx * speed, y: ny * speed, z: 0 });
      ptzDragStartRef.current = { x: e.clientX, y: e.clientY };
      return;
    }
    if (!isPanning || zoom <= 1) return;
    const el = containerRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;

    const start = panStartRef.current;
    // Convertir desplazamiento a porcentaje del contenedor
    const dxPct = ((e.clientX - start.x) / rect.width) * 100;
    const dyPct = ((e.clientY - start.y) / rect.height) * 100;

    // Ajustar sensibilidad por zoom para que se sienta controlable
    const factor = 1 / zoom;
    const nextX = start.originX - dxPct * factor;
    const nextY = start.originY - dyPct * factor;

    setZoomOrigin({
      x: Math.min(100, Math.max(0, nextX)),
      y: Math.min(100, Math.max(0, nextY)),
    });
  }, [isPanning, isPtzDragging, zoom, ptzMove]);

  const stopPan = useCallback((e?: React.PointerEvent) => {
    if (isPtzDragging) {
      ptzStop();
      ptzDragStartRef.current = null;
      setIsPtzDragging(false);
      const el = containerRef.current;
      if (el && e?.pointerId != null) {
        try {
          el.releasePointerCapture(e.pointerId);
        } catch {
          // ignore
        }
      }
      return;
    }
    if (!isPanning) return;
    const el = containerRef.current;
    if (el && e?.pointerId != null) {
      try {
        el.releasePointerCapture(e.pointerId);
      } catch {
        // ignore
      }
    }
    setIsPanning(false);
  }, [isPanning, isPtzDragging, ptzStop]);

  return (
    <div 
      className="video-card"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={`video-card-header ${isHovered ? 'visible' : 'hidden'}`}>
        <div className="video-header-actions">
          {onReconnect && (
            <button
              type="button"
              className="video-header-btn video-header-btn-refresh"
              onClick={() => onReconnect(camera.id)}
              title="Refrescar stream"
            >
              <RefreshCw size={13} />
            </button>
          )}
          <button
            type="button"
            className="video-header-btn"
            onClick={handleFullscreen}
            title="Pantalla completa"
          >
            ⛶
          </button>
          {onClose && (
            <button
              type="button"
              className="video-header-btn video-header-btn-danger"
              onClick={onClose}
              title="Cerrar"
            >
              ×
            </button>
          )}
        </div>
      </div>

      <div
        ref={containerRef}
        className={`video-container ${zoom > 1 ? 'zoomed' : ''} ${isPanning ? 'panning' : ''} ${showPtz ? 'ptz-active' : ''} ${isPtzDragging ? 'ptz-dragging' : ''}`}
        onWheel={handleWheelZoom}
        onDoubleClick={handleContainerDoubleClick}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMovePan}
        onPointerUp={stopPan}
        onPointerCancel={stopPan}
        onPointerLeave={stopPan}
        title={
          camera.isOnvif && showPtz
            ? 'Arrastrar para mover PTZ · Rueda: zoom óptico · Doble clic: Home'
            : zoom > 1
              ? `Zoom: ${Math.round(zoom * 100)}% (doble clic para resetear)`
              : 'Rueda del mouse para hacer zoom (doble clic para resetear)'
        }
      >
        <div className="video-title-bar">
          <span className="video-title-name">{camera.name}</span>
          <span className="video-title-status">
            <span className="status-dot" style={{ backgroundColor: getStatusColor() }} />
            <span className="video-title-status-text">{getStatusText()}</span>
          </span>
        </div>
        {livePlayerUrl && isMediaMtxHtmlPlayerUrl(livePlayerUrl) ? (
          <>
            {tabVisible ? (
              <iframe
                title={`stream-${camera.id}`}
                className="video-player video-player-iframe"
                src={toMediaMtxHtmlPlayerSrc(livePlayerUrl)}
                allow="autoplay; fullscreen; camera; microphone"
                referrerPolicy="no-referrer"
                tabIndex={-1}
                style={{
                  border: 0,
                  transform: `scale(${zoom})`,
                  transformOrigin: `${zoomOrigin.x}% ${zoomOrigin.y}%`,
                }}
                onLoad={() => {
                  // onLoad = HTML listo; el WebRTC interno puede tardar aún (cold start).
                  setError(null);
                  window.setTimeout(() => setIsLoading(false), 8000);
                }}
              />
            ) : (
              <div className="video-player video-player-iframe" style={{ background: '#0a0a0a' }} />
            )}
            {/* Capa sobre el iframe: recupera PTZ arrastrar/soltar y zoom digital (como con WHEP). */}
            <div className="video-interaction-overlay" aria-hidden />
          </>
        ) : (
          <video
            ref={videoRef}
            className="video-player"
            autoPlay
            muted
            playsInline
            controls={false}
            style={{
              transform: `scale(${zoom})`,
              transformOrigin: `${zoomOrigin.x}% ${zoomOrigin.y}%`,
            }}
          >
            Tu navegador no soporta la reproducción de video.
          </video>
        )}
        
        {(!livePlayerUrl || error || (isLoading && !isReconnecting) || isReconnecting || camera.status === 'crashed' || camera.status === 'error') && (
          <div className="video-placeholder">
            {(isReconnecting || (camera.status === 'crashed' && reconnectAttempts < 3)) ? (
              <>
                <div className="spinner"></div>
                <div className="placeholder-text">
                  Conexión perdida. Reconectando... ({reconnectAttempts}/3)
                </div>
              </>
            ) : (camera.status === 'crashed' || camera.status === 'error') && reconnectAttempts >= 3 ? (
              <>
                <div className="placeholder-icon"><TriangleAlert size={32} strokeWidth={1.5} /></div>
                <div className="placeholder-text" style={{ color: '#ef4444', fontWeight: 600 }}>
                  Conexión fallida después de {reconnectAttempts} intentos
                </div>
                <div className="placeholder-hint">
                  Usa el botón "Reconectar" para intentar nuevamente
                </div>
              </>
            ) : isLoading && !error ? (
              <>
                <XTAMSpinner variant="small" />
                <div className="placeholder-text">
                  {camera.status === 'starting' || !tabVisible
                    ? 'Conectando…'
                    : 'Cargando stream...'}
                </div>
              </>
            ) : (
              <>
                <div className="placeholder-icon"><Video size={32} strokeWidth={1.5} /></div>
                <div className="placeholder-text">
                  {(() => {
                    const raw = (error ?? '').trim();
                    if (!raw) return 'Sin stream disponible';
                    if (/no stream is available/i.test(raw)) return 'SIN SEÑAL';
                    if (/^\s*\{\s*"status"\s*:\s*"error"/.test(raw) && /"error"\s*:.*no stream/i.test(raw)) return 'SIN SEÑAL';
                    try {
                      const parsed = JSON.parse(raw) as { status?: string; error?: string };
                      if (parsed?.status === 'error' && typeof parsed?.error === 'string' && /no stream/i.test(parsed.error)) return 'SIN SEÑAL';
                    } catch {
                      /* not JSON */
                    }
                    return raw;
                  })()}
                </div>
              </>
            )}
            {camera.status === 'active' && !camera.streamUrl && !isReconnecting && !isLoading && !error && (
              <div className="placeholder-hint">
                Inicia la cámara para ver el stream
              </div>
            )}
          </div>
        )}

      </div>

      <div className={`video-controls ${isHovered ? 'visible' : 'hidden'}`}>
        {camera.isOnvif && (
          <>
            <button
              type="button"
              className={`ptz-toggle-btn ${showPtz ? 'active' : ''}`}
              onClick={() => setShowPtz((v) => !v)}
              title="Controles PTZ: arrastrar en el video para mover, rueda para zoom óptico"
            >
              PTZ
            </button>
            {showPtz && (
              <button
                type="button"
                className="ptz-home-btn"
                onClick={ptzHome}
                title="Ir a posición Home"
              >
                ⊙ Home
              </button>
            )}
          </>
        )}

        {((camera.status === 'crashed' || camera.status === 'error' || reconnectAttempts >= 3) || !!error) && onReconnect && (
          <button
            className="reconnect-btn"
            onClick={() => onReconnect(camera.id)}
            title="Reconectar stream"
          >
            <RefreshCw size={14} /> Reconectar
          </button>
        )}
      </div>

      {camera.isOnvif && showPtz && ptzError && (
        <div className="ptz-error-toast" role="alert">
          {ptzError}
        </div>
      )}
    </div>
  );
};

export default VideoCard;
